import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export const SITE_URL = process.env.REACT_APP_SITE_URL;

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo(pageKey, override = {}) {
  const { site } = useSite() || {};
  const loc = useLocation();
  const base = site?.settings?.seo?.[pageKey] || {};
  const title = override.title || base.title || "Ironbark Construction";
  const description = override.description || base.description || "";
  const image = override.image || base.image || "/media/hero.jpg";
  const noindex = override.noindex || base.noindex;
  const ld = override.jsonLd ? JSON.stringify(override.jsonLd) : "";
  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Ironbark Construction");
    setMeta("property", "og:url", `${SITE_URL}${loc.pathname}`);
    const abs = mediaUrl(image, "lg");
    setMeta("property", "og:image", abs.startsWith("http") ? abs : `${SITE_URL}${abs}`);
    setMeta("name", "twitter:card", "summary_large_image");
    setLink("canonical", `${SITE_URL}${loc.pathname}`);
    let s = document.getElementById("page-ld");
    if (ld) {
      if (!s) {
        s = document.createElement("script");
        s.type = "application/ld+json";
        s.id = "page-ld";
        document.head.appendChild(s);
      }
      s.textContent = ld;
    } else if (s) {
      s.remove();
    }
  }, [title, description, image, noindex, ld, loc.pathname]);
}

export function usePageTracking() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) return;
    let vid = localStorage.getItem("ib_vid");
    if (!vid) {
      vid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("ib_vid", vid);
    }
    api.post("/public/track", { path: loc.pathname, ref: document.referrer, vid }).catch(() => {});
    window.scrollTo(0, 0);
  }, [loc.pathname]);
}

export function localBusinessLd(company, areas = [], site = {}) {
  const g = site.settings?.google_reviews || {};
  return {
    "@type": "GeneralContractor",
    "@id": `${SITE_URL}/#business`,
    name: company.name,
    telephone: company.phone,
    email: company.email,
    url: SITE_URL,
    image: `${SITE_URL}/media/hero.jpg`,
    logo: `${SITE_URL}/media/logo.png`,
    description: `${company.name} is a licensed Florida general contractor (Lic. #${company.license}) based in ${company.city}, FL${company.experience ? ` with ${company.experience}` : ""}, building custom homes, metal buildings, remodels and additions.`,
    address: { "@type": "PostalAddress", addressLocality: company.city, addressRegion: company.state, addressCountry: "US" },
    openingHoursSpecification: [{ "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "16:00" }],
    areaServed: areas.map((a) => ({ "@type": "City", name: `${a.name}, FL` })),
    employee: { "@type": "Person", name: company.president, jobTitle: "President" },
    hasCredential: { "@type": "EducationalOccupationalCredential", credentialCategory: "license", name: `Florida General Contractor License ${company.license}` },
    ...(g.url ? { hasMap: g.url, sameAs: [g.url] } : {}),
    ...(g.count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: g.rating, reviewCount: g.count } } : {}),
    makesOffer: (site.services || []).map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s.title, url: `${SITE_URL}/services/${s.slug}` } })),
  };
}

export function breadcrumbLd(list) {
  return { "@type": "BreadcrumbList", itemListElement: [["Home", "/"], ...list].map(([name, u], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${u}` })) };
}

export function faqLd(faqs) {
  return { "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })) };
}

export const graph = (...items) => ({ "@context": "https://schema.org", "@graph": items });
